// Build CASL ability from user role/permissions with dynamic department-based access control
import { AbilityBuilder, createMongoAbility } from '@casl/ability';
import { parsePermission, canAccessDepartment, getDepartmentFromSubject, getUserMetadata } from './constants';

/**
 * Builds CASL ability based on user's department, role, and permissions
 * All data comes dynamically from the user object - no hardcoded mappings
 * 
 * Expected user object structure:
 * {
 *   department: 'sales-depart',        // User's department
 *   role: 'sales-head',                // User's role
 *   permissions: ['dashboard.view', 'leads.read', ...],  // Array of permission strings
 *   adminRoles: ['admin', 'sub-admin'], // Optional: admin-level roles (defaults to ['admin', 'sub-admin'])
 *   resourceDepartments: {              // Optional: mapping of resources to departments
 *     'Leads': 'sales-depart',
 *     'Dispatch': 'dispatch-depart',
 *     ...
 *   }
 * }
 */
const buildAbilityFromUser = (user) => {
    const { can, cannot, build } = new AbilityBuilder(createMongoAbility);

    if (!user) {
        // No user = guest permissions (no access)
        return build();
    }

    const {
        department: userDepartment,
        role: userRole,
        permissions: userPermissions,
        adminRoles,
        resourceDepartments
    } = getUserMetadata(user);

    // Admin roles get full access to all departments
    if (adminRoles.includes(userRole)) {
        can('manage', 'all');
        return build();
    }

    // Process permissions from backend
    // Each permission is in format: 'resource.action' (e.g., 'dashboard.view', 'leads.read')
    userPermissions.forEach((permissionString) => {
        const parsed = parsePermission(permissionString);
        if (parsed) {
            const [action, subject] = parsed;
            
            // Check if this resource belongs to a specific department
            const subjectDepartment = getDepartmentFromSubject(subject, resourceDepartments);
            
            if (subjectDepartment) {
                // Resource belongs to a department - check access
                if (canAccessDepartment(userDepartment, subjectDepartment, userRole, adminRoles)) {
                    can(action, subject);
                }
                // If department check fails, permission is denied (implicit)
            } else {
                // Resource doesn't belong to a specific department (department-agnostic)
                // Examples: Dashboard, Notifications, Calendar, Profile, Settings
                // Allow access if user has the permission
                can(action, subject);
            }
        }
    });

    // Common permissions for all authenticated users (regardless of department)
    if (userRole !== 'guest') {
        can('read', 'Profile');
        can('update', 'Profile');
        can('read', 'Settings');
        can('update', 'Settings');
    }

    return build();
};

export { buildAbilityFromUser };
